// Entornos (`data.type === 'environment'`): las variables de un .env guardadas en el vault. Plan: design/SETTINGS-DEVELOPER-PRIVACY.md 5.3.2.
//
//   data = { type: 'environment', variables: [{ key, value? | ref? }], extends?: 'nombre de otro entorno', notes }
//
// `value` es un secreto guardado en el propio entorno; `ref` una referencia `sgc://...` a un campo de otro item. El nombre
// del entorno es el titulo del item. Las reglas son las del servidor (server/services/secretRefs.js, validateEnvironment):
// aqui se comprueban antes de guardar para avisar en la fila; el servidor las vuelve a comprobar.
import { dotenvValue, ENV_KEY_RE } from './dotenv';

export { ENV_KEY_RE };

export const ENV_KEY_MAX = 128;
export const REF_PREFIX = 'sgc://';

export const isRefText = (v) => typeof v === 'string' && v.slice(0, REF_PREFIX.length).toLowerCase() === REF_PREFIX;

// Mismo escapado que la CLI y el servidor: / % ? # espacios y controles.
// eslint-disable-next-line no-control-regex
const encodeSegment = (s) => String(s).replace(/[%/?#\s\u0000-\u001f\u007f]/g, (c) => encodeURIComponent(c));

/** sgc://<bóveda>/<item>/<campo> (bóveda e item por id o nombre). */
export function formatRef({ vault, item, field }) {
  return `${REF_PREFIX}${[vault, item, field].map(encodeSegment).join('/')}`;
}

/** ¿Tiene una referencia la forma de una? (tres segmentos, o org/<org>/<bóveda>/<item>/<campo>). La resolucion es del servidor. */
export function looksLikeRef(text) {
  if (!isRefText(text) || text.length > 1024 || /[?#]/.test(text)) return false;
  const parts = text.slice(REF_PREFIX.length).split('/');
  if (parts.some((p) => p === '')) return false;
  return parts.length === 3 || (parts.length === 5 && parts[0].toLowerCase() === 'org');
}

/** Variables tal como las edita el formulario: cada fila con un `mode` ('value' | 'ref') y un id para la tabla. */
export function toRows(variables) {
  let n = 0;
  return (Array.isArray(variables) ? variables : [])
    .filter((v) => v && typeof v === 'object' && typeof v.key === 'string')
    .map((v) => ({
      id: `r${n++}`,
      key: v.key,
      mode: v.ref || isRefText(v.value) ? 'ref' : 'value',
      value: v.ref ? '' : (isRefText(v.value) ? '' : String(v.value ?? '')),
      ref: v.ref ? String(v.ref) : (isRefText(v.value) ? String(v.value) : '')
    }));
}

/** Lo que se guarda: `{ key, value }` o `{ key, ref }`; sin filas vacias ni espacios en las claves. */
export function fromRows(rows) {
  return rows
    .map((r) => ({ key: String(r.key || '').trim(), mode: r.mode, value: r.value ?? '', ref: String(r.ref || '').trim() }))
    .filter((r) => r.key !== '' || (r.mode === 'ref' ? r.ref !== '' : r.value !== ''))
    .map((r) => (r.mode === 'ref' ? { key: r.key, ref: r.ref } : { key: r.key, value: r.value }));
}

/**
 * Problemas de las filas, por indice: { [index]: 'empty_key' | 'invalid_key' | 'duplicate_key' | 'invalid_ref' }.
 * Una fila sin nada escrito no es un problema (se descarta al guardar).
 */
export function rowProblems(rows) {
  const problems = {};
  const seen = new Map();
  rows.forEach((r, i) => {
    const key = String(r.key || '').trim();
    const hasContent = r.mode === 'ref' ? String(r.ref || '').trim() !== '' : String(r.value || '') !== '';
    if (key === '') { if (hasContent) problems[i] = 'empty_key'; return; }
    if (!ENV_KEY_RE.test(key) || key.length > ENV_KEY_MAX) { problems[i] = 'invalid_key'; return; }
    if (seen.has(key)) { problems[i] = 'duplicate_key'; if (problems[seen.get(key)] === undefined) problems[seen.get(key)] = 'duplicate_key'; return; }
    seen.set(key, i);
    if (r.mode === 'ref' && String(r.ref || '').trim() !== '' && !looksLikeRef(String(r.ref).trim())) problems[i] = 'invalid_ref';
  });
  return problems;
}

/** Filas que salen de las variables importadas de un .env: un valor `sgc://...` es una referencia. */
export function rowsFromImport(entries) {
  return entries.map((e) => (isRefText(e.value) ? { key: e.key, ref: e.value } : { key: e.key, value: e.value }));
}

/**
 * Mezcla variables importadas con las que ya hay. `replace`: las que ya existen se sustituyen (conservan su sitio);
 * si no, se dejan como estan. Devuelve { variables, added, replaced, skipped }.
 */
export function mergeImported(existing, imported, { replace }) {
  const out = existing.map((v) => ({ ...v }));
  const at = new Map(out.map((v, i) => [v.key, i]));
  let added = 0; let replaced = 0; let skipped = 0;
  for (const v of imported) {
    if (at.has(v.key)) {
      if (replace) { out[at.get(v.key)] = v; replaced += 1; } else skipped += 1;
    } else { at.set(v.key, out.length); out.push(v); added += 1; }
  }
  return { variables: out, added, replaced, skipped };
}

/**
 * Plantilla `.env` de un entorno: cada variable como una referencia (la suya propia si es un valor, la que ya es si es
 * una referencia). No lleva ningun valor: se puede guardar en un repositorio y usar con `sgc run --env-file`.
 */
export function templateText({ id, vault, variables }) {
  return `${(variables || []).filter((v) => v.key).map((v) => `${v.key}=${dotenvValue(v.ref || (isRefText(v.value) ? v.value : formatRef({ vault: vault || 'personal', item: id, field: v.key })))}`).join('\n')}\n`;
}

/** `.env` con valores en claro a partir de { KEY: value } (lo que devuelve GET /api/env/:name). */
export function dotenvText(values) {
  return `${Object.entries(values).map(([k, v]) => `${k}=${dotenvValue(v)}`).join('\n')}\n`;
}

/** La orden de la CLI para usar un entorno, con el nombre escapado para una shell. */
export function cliCommand(name) {
  const quoted = /^[A-Za-z0-9_.-]+$/.test(name) ? name : `'${String(name).replace(/'/g, `'\\''`)}'`;
  return `sgc run --env ${quoted} -- <command>`;
}
